import { iriLocalName } from "../../../shared/references/shortReferences";
export type OntologyEntityType =
  | "classes"
  | "objectProperties"
  | "dataProperties"
  | "annotationProperties"
  | "individuals";

export type OntologyDisplayMode = "structured" | "raw";

export type OntologyViewerPane = OntologyEntityType | "relations";

export interface LocalizedValue {
  value: string;
  language?: string;
}

export interface OntologyEntity {
  iri: string;
  type: OntologyEntityType;
  label: LocalizedValue[];
  definition: LocalizedValue[];
  parents: string[];
  children: string[];
  domain: string[];
  range: string[];
}

export interface OntologyViewerSource {
  /** The model version shown: `<project content id>@<commit>`. */
  version: string;
}

export interface OntologyViewerModel {
  source: OntologyViewerSource;
  entitiesByType: Record<OntologyEntityType, OntologyEntity[]>;
}

export const ONTOLOGY_ENTITY_TYPES: OntologyEntityType[] = [
  "classes",
  "objectProperties",
  "dataProperties",
  "annotationProperties",
  "individuals",
];

export const ONTOLOGY_VIEWER_PANES: OntologyViewerPane[] = [
  ...ONTOLOGY_ENTITY_TYPES,
  "relations",
];

/** The root of each tree. The class and property roots are OWL's own; the annotation
 * property and individual roots only group their entries. */
export const ONTOLOGY_ROOT_IRIS: Record<OntologyEntityType, string> = {
  classes: "http://www.w3.org/2002/07/owl#Thing",
  objectProperties: "http://www.w3.org/2002/07/owl#topObjectProperty",
  dataProperties: "http://www.w3.org/2002/07/owl#topDataProperty",
  annotationProperties: "urn:datamodel-workflow:ontology-viewer:topAnnotationProperty",
  individuals: "urn:datamodel-workflow:ontology-viewer:individuals",
};

const ROOT_NAMES: Record<string, string> = {
  [ONTOLOGY_ROOT_IRIS.classes]: "owl:Thing",
  [ONTOLOGY_ROOT_IRIS.objectProperties]: "owl:topObjectProperty",
  [ONTOLOGY_ROOT_IRIS.dataProperties]: "owl:topDataProperty",
  [ONTOLOGY_ROOT_IRIS.annotationProperties]: "Annotation Properties",
  [ONTOLOGY_ROOT_IRIS.individuals]: "Individuals",
};

/**
 * The first level of a kind's tree: what its root holds, or, without the root, the entries
 * without a parent. The root itself is no row: the chosen kind already names the tree, and
 * OWL's roots carry nothing to read (F77).
 */
export function topLevelEntities(entities: readonly OntologyEntity[], entityType: OntologyEntityType): OntologyEntity[] {
  const rootIri = ONTOLOGY_ROOT_IRIS[entityType];
  const root = entities.find((entity) => entity.iri === rootIri);
  const byIri = new Map(entities.map((entity) => [entity.iri, entity]));
  const top =
    root === undefined
      ? entities.filter((entity) => entity.parents.length === 0)
      : root.children.map((iri) => byIri.get(iri)).filter((entity): entity is OntologyEntity => entity !== undefined);
  return top.sort((left, right) => entityDisplayName(left).localeCompare(entityDisplayName(right)));
}

export function isOntologyRootIri(iri: string): boolean {
  return iri in ROOT_NAMES;
}

export function isOntologyEntityType(value: string | null): value is OntologyEntityType {
  return ONTOLOGY_ENTITY_TYPES.includes(value as OntologyEntityType);
}

export function isOntologyViewerPane(value: string | null): value is OntologyViewerPane {
  return ONTOLOGY_VIEWER_PANES.includes(value as OntologyViewerPane);
}

export function isOntologyDisplayMode(value: string | null): value is OntologyDisplayMode {
  return value === "structured" || value === "raw";
}

/** An IRI as the ontology viewer names it: the root names, else its local name. */
export function compactIri(iri: string): string {
  return ROOT_NAMES[iri] ?? iriLocalName(iri);
}

export function entityDisplayName(entity?: Pick<OntologyEntity, "iri" | "label"> | null): string {
  if (!entity) {
    return "";
  }
  return entity.label[0]?.value || compactIri(entity.iri);
}
